import { useEffect } from 'react';
import usePrevious from '@elementary/common-ui/hooks/usePrevious';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { useAuth } from '@frontegg/react';
import { useQuery } from '@tanstack/react-query';
import { PULL_REQUESTS } from '@elementary/common-ui/constants/queryKeys';
import { useLocation } from 'react-router-dom';
import useFlags from 'shared/hooks/useFlags';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from '../services/api';
import useNavigateWithEnv from './useNavigateWithEnv';
import useSnackbar from './useSnackbar';
import { useCodeRepositoryIntegration } from './integrations';

const usePendingChangesPolling = () => {
  const { user } = useAuth();
  const { hasFlag } = useFlags();
  const hasPermission = useEnvPermission();
  const selectedEnvId = useSelectedEnv();
  const { isRepositoryConnected } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });
  const navigate = useNavigateWithEnv();
  const { pathname } = useLocation();
  const isPullRequestPage = pathname?.includes('/pull-request');

  const { data } = useQuery(
    [PULL_REQUESTS, selectedEnvId],
    () => api.fetchPullRequests(selectedEnvId!),
    {
      enabled:
        !!selectedEnvId &&
        hasFlag('test_management') &&
        hasFlag('code_project') &&
        hasPermission('VIEW_PULL_REQUESTS') &&
        !!isRepositoryConnected,
      refetchInterval: 10000,
      keepPreviousData: true,
    },
  );

  const prevData = usePrevious(data);

  const { openSnackbar } = useSnackbar();

  useEffect(() => {
    const prevPendingPrs = prevData?.pull_requests.filter(
      (pr) => pr.created_by === user?.email && pr.status === 'pending',
    );

    const newNonPendingPrs = data?.pull_requests.filter(
      (pr) =>
        pr.status !== 'pending' &&
        prevPendingPrs?.find((prevPR) => prevPR.id === pr.id),
    );

    if (newNonPendingPrs?.length) {
      const isError = newNonPendingPrs?.[0].status === 'error';

      openSnackbar({
        message: isError
          ? 'The pull request creation has failed'
          : 'The pull request was created successfully! Changes will take place once the PR is merged',
        variant: isError ? 'error' : 'success',
        duration: 10000,
        actions: [
          {
            text: isError ? 'Contact support' : 'View pull request',
            cb: () =>
              window.open(
                isError
                  ? 'mailto:support@elementary-data.com'
                  : newNonPendingPrs?.[0].url,
              ),
          },
          ...(!isPullRequestPage
            ? [
                {
                  text: 'View status',
                  cb: () => navigate('/pull-requests'),
                },
              ]
            : []),
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
};

export default usePendingChangesPolling;
